<!-- Note: This component is being used for focused onboarding -->
<!-- We plan to reuse this for future flows/replace old flows (e.g. job post) -->
<!-- It will be added to storybook once designs are finalized -->

<template>
  <section>
    <!-- Progress Bar(s) -->
    <div
      class="bg-grey-background-subdued sm:bg-white p-16 sm:box-shadow-sm transition-text"
      :class="!showTransitionText && 'collapsed'"
    >
      <p v-if="progressBarTitle" class="text-overline text-grey-header">{{ progressBarLabel }}</p>
      <slot name="progressBar" :currentStep="currentStep" :maxSteps="steps.length + 1" />
      <transition :name="back ? 'back' : 'text'">
        <p v-if="showTransitionText" class="mt-8 text-caption">{{ step.transitionText || 'Great' }}</p>
      </transition>
    </div>
    <div class="fixed-height flex justify-center">
      <transition :name="back ? 'back' : 'slide'">
        <div v-show="showCard" class="sm:mt-16 fixed-height" :class="cardWidthClass">
          <!-- Breadcrumb + Header -->
          <div class="w-full pl-16 sm:pl-0">
            <Breadcrumb
              v-if="!isFirstStep"
              :aria-label="`Back to ${breadcrumbHeader}`"
              class="mb-16"
              :header="breadcrumbHeader"
              :isButton="true"
              @click="previousStep"
            />
            <h1 class="text-h2 mr-12">{{ step.title }}</h1>
            <p v-if="step.required" class="text-small text-grey-text-subdued mt-4">Required</p>
          </div>
          <!-- Stepper Content -->
          <Card class="form-stepper-card sm:mt-16 box-shadow-none sm:box-shadow-sm sm:p-24">
            <component
              :is="step.component"
              v-if="step.component"
              ref="stepComponent"
              class="step-component"
              :class="step.xlCard && 'xl'"
            />
            <div v-else>{{ step.title }} content</div>
            <div class="sm:mt-24" :class="stickyButtonWidthClass">
              <StickyButton :value="buttonValue" :loading="loading" @click="handleAction" />
            </div>
          </Card>
        </div>
      </transition>
    </div>
  </section>
</template>

<script lang="ts">
import Breadcrumb from '../breadcrumb/Breadcrumb.vue';
import Card from '../card/Card.vue';
import StickyButton from '../sticky_button/StickyButton.vue';

interface Step {
  name: string;
  title: string;
  required: boolean;
  component: any;
  alwaysFullWidthButton: boolean;
  xlCard: boolean;
  nextStepCallback: () => void;
  previousStepCallback: () => void;
}

export default {
  name: 'FormStepper',
  components: { Breadcrumb, Card, StickyButton },
  props: {
    progressBarTitle: {
      type: String,
      default: '',
    },
    progressBarLastStepTitle: {
      type: String,
      default: '',
    },
    steps: {
      type: Array<Step>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      back: false,
      showTransitionText: false,
      showCard: true,
      currentStep: 1,
    };
  },
  computed: {
    cardWidthClass(): String {
      return this.steps[this.currentStep - 1].xlCard ? 'max-w-688' : 'max-w-488';
    },
    stickyButtonWidthClass(): String {
      return this.steps[this.currentStep - 1].alwaysFullWidthButton ? '' : 'lg:max-w-max';
    },
    step(): Step {
      return this.steps[this.currentStep - 1];
    },
    progressBarLabel(): string {
      return this.isLastStep ? this.progressBarLastStepTitle : this.progressBarTitle;
    },
    breadcrumbHeader(): string {
      return this.steps[this.currentStep - 2]?.name;
    },
    isFirstStep(): boolean {
      return this.currentStep === 1;
    },
    isLastStep(): boolean {
      return this.currentStep === this.steps.length;
    },
    buttonValue(): string {
      if (this.isLastStep) {
        return 'Save & Continue';
      }
      return 'Next';
    },
  },
  watch: {
    currentStep() {
      this.scrollToTop();
    },
  },
  methods: {
    previousStep() {
      this.back = true;
      this.step.previousStepCallback();
      this.currentStep -= 1;
    },
    nextStep() {
      this.scrollToTop();
      this.back = false;
      this.showTransitionText = true;
      this.showCard = false;
      setTimeout(() => {
        this.showCard = true;
        this.currentStep += 1;
        this.handleFocus();
        this.showTransitionText = false;
      }, 950);
    },
    validateStep() {
      const component = this.$refs.stepComponent as { validate?: () => boolean };
      return component?.validate ? component.validate() : true;
    },
    handleFocus() {
      this.$nextTick(() => {
        const component = this.$refs.stepComponent as { focusElement?: () => void };
        component?.focusElement?.();
      });
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      });
    },
    handleAction() {
      const isValid = this.validateStep();

      if (!isValid) {
        this.handleFocus();
        return;
      }
      this.step.nextStepCallback();
      if (this.isLastStep) {
        this.$emit('submit');
      } else {
        this.nextStep();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.form-stepper-card {
  @media (max-width: 480px) {
    background-color: #fbfbfb !important;
  }
}

// Animate the height change of the box with the transition text
.transition-text {
  @media only screen and (min-width: 480px) {
    transition: all 0.75s;
    overflow: hidden;
    height: 96px; // height has to be specified to animate height change
    &.collapsed {
      transition: all 0.75s;
      height: 70px;
    }
  }
}

// Preserve height so content does not jump (especially mobile sticky button, footer)
.fixed-height {
  min-height: calc(100vh - 118px);

  @media only screen and (min-width: 480px) {
    height: auto;
    min-height: calc(100vh - 118px);
  }

  // main class margin change breakpoint
  @media only screen and (min-width: 768px) {
    min-height: calc(100vh - 150px);
  }
}

// Preserve width so that flexbox content does not jump (e.g. toggle token group components)
.step-component {
  width: calc(100vw - 32px);
  @media only screen and (min-width: 480px) {
    &:not(.xl) {
      width: 432px;
    }
    &.xl {
      width: calc(100vw - 72px);
    }
  }

  @media only screen and (min-width: 688px) {
    &.xl {
      width: 620px;
    }
  }
}

// Disappear content & slide in new content
.slide-leave-active {
  position: fixed;
  transition: 400ms;
  opacity: 0;
}

.slide-enter-active {
  position: fixed;
  transition-duration: 250ms;
}

.slide-enter {
  opacity: 0;
  transform: translate(100%, 0);
}

// Transition for text under progress bar
.text-leave-active {
  position: fixed;
  transition: 500ms;
}

.text-enter-active {
  position: fixed;
  transition: 0s;
}

.text-leave-to {
  transform: translate(-200%, 0);
}

// Back should not have transition
.back-leave-active,
.back-enter-active {
  transition: 0ms;
  position: fixed;
}
.back-enter {
  position: fixed;
}
.back-leave-to {
  position: fixed;
}
</style>
